import React, { useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Paperclip,
  PlusCircle,
  Ticket,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { CSPUTILS } from "../config/API";
import { withSanitizedFileName } from "../utils/sanitizeFileName";

const PageShell = ({ children }) => (
  <div className="max-w-4xl mx-auto">
    <div className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-sm">
      {children}
    </div>
  </div>
);

const PageHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="relative border-b border-gray-100 bg-gradient-to-r from-primary/5 via-white to-white px-5 py-4 sm:px-6">
    <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-primary via-primary/80 to-primary/40" />
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon className="h-4 w-4" strokeWidth={2} />
      </div>
      <div>
        <h1 className="text-lg font-bold tracking-tight text-gray-900 sm:text-xl">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-0.5 max-w-2xl text-xs text-gray-500 sm:text-sm">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  </div>
);

const inputClassName =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20";

const selectClassName =
  "w-full appearance-none rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20";

const textareaClassName =
  "w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20";

const FormField = ({ label, required, children, className = "" }) => (
  <div className={className}>
    <label className="mb-1.5 block text-sm font-medium text-gray-700">
      {label}
      {required && <span className="ml-0.5 text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const SUPPORT_TYPE_OPTIONS = [
  "Eng Mgt",
  "DFF",
  "SFI (SOW Filler Item)",
  "IRD (Strategic IR&D Item)",
  "IBF (Issue and Bug Fix Item)",
  "CWI (Contractual WBS Item)",
  "SMR (Standard Maintenance Release Item)",
  "Sales Action",
  "Tribe Water April",
  "Phone Support",
  "Executive Actions",
  "Email Support",
  "Job Jar",
  "Management",
  "CM",
  "20",
  "SRR",
];

const STATUS_OPTIONS = ["Open", "Closed", "Non-Issue"];

const INITIAL_FORM = {
  supporttype: "",
  reportingparty: "",
  module: "",
  issuedescription: "",
  status: "Open",
  statusdate: new Date().toISOString().split("T")[0],
  solutiondescription: "",
  techcomments: "",
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / k ** i) * 100) / 100} ${sizes[i]}`;
};

const SuccessModal = ({ data, onClose }) => {
  if (!data) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
        <div className="flex flex-col items-center px-6 py-8 text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Success</h2>
          <p className="mt-2 text-sm text-gray-600">{data.message}</p>
          <button
            type="button"
            onClick={onClose}
            className="mt-6 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-primary/90"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const AddTicket = () => {
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [successModal, setSuccessModal] = useState(null);
  const [fileError, setFileError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setSubmitError(null);
    setSuccessModal(null);
  };

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const maxSize = 50 * 1024 * 1024;
    const validFiles = files.filter((file) => file.size <= maxSize);
    const skipped = files.length - validFiles.length;

    if (skipped > 0) {
      setFileError(`${skipped} file${skipped !== 1 ? "s were" : " was"} skipped (max 50MB per file).`);
    } else {
      setFileError(null);
    }

    if (!validFiles.length) return;

    setAttachments((prev) => [
      ...prev,
      ...validFiles.map((file) => ({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
        file,
      })),
    ]);
  };

  const handleFileChange = (e) => {
    addFiles(e.target.files);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    addFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => prev.filter((item) => item.id !== id));
    setFileError(null);
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM);
    setAttachments([]);
    setSubmitError(null);
    setSuccessModal(null);
    setFileError(null);
  };

  const closeSuccessModal = () => setSuccessModal(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    setSuccessModal(null);

    try {
      const payload = new FormData();
      payload.append("action", "submit");
      payload.append("supporttype", formData.supporttype);
      payload.append("reportingparty", formData.reportingparty);
      payload.append("module", formData.module);
      payload.append("issuedescription", formData.issuedescription);
      payload.append("status", formData.status);
      payload.append("statusdate", formData.statusdate);
      payload.append("solutiondescription", formData.solutiondescription);
      payload.append("techcomments", formData.techcomments);

      attachments.forEach((item, index) => {
        payload.append(`artifacttype[${index}]`, withSanitizedFileName(item.file));
      });

      const response = await fetch(CSPUTILS, {
        method: "POST",
        body: payload,
      });

      const responseText = await response.text();
      console.log("Add Ticket API response:", responseText);

      let result;

      try {
        result = JSON.parse(responseText);
        console.log("Add Ticket API parsed response:", result);
      } catch {
        if (!response.ok) {
          throw new Error(responseText || `HTTP error! status: ${response.status}`);
        }
        resetForm();
        setSuccessModal({
          message: "Ticket has been submitted",
          itemid: null,
        });
        return;
      }

      if (!response.ok || result?.status === "error") {
        throw new Error(result?.message || "Failed to create ticket. Please try again.");
      }

      if (result?.status === "success") {
        resetForm();
        setSuccessModal({
          message: result.message || "Ticket has been submitted",
          itemid: result.itemid ?? null,
        });
        return;
      }

      throw new Error(result?.message || "Failed to create ticket. Please try again.");
    } catch (err) {
      console.error("Error creating ticket:", err);
      setSubmitError(err.message || "Failed to create ticket. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      <PageHeader
        icon={PlusCircle}
        title="Add Ticket"
        subtitle="Submit a new support ticket to the TribeVue Support Portal."
      />

      <form onSubmit={handleSubmit} className="p-5 sm:p-6">
        {submitError && (
          <div className="mb-5 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <p>{submitError}</p>
          </div>
        )}

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <FormField label="Support Type" required>
            <div className="relative">
              <select
                name="supporttype"
                value={formData.supporttype}
                onChange={handleChange}
                required
                className={selectClassName}
              >
                <option value="">Select support type</option>
                {SUPPORT_TYPE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </FormField>

          <FormField label="Status" required>
            <div className="relative">
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
                className={selectClassName}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </FormField>

          <FormField label="Reporting Party" required>
            <input
              type="text"
              name="reportingparty"
              value={formData.reportingparty}
              onChange={handleChange}
              required
              placeholder="Enter reporting party name"
              className={inputClassName}
            />
          </FormField>

          <FormField label="Module" required>
            <input
              type="text"
              name="module"
              value={formData.module}
              onChange={handleChange}
              required
              placeholder="e.g. Finance, HR, Enrollment"
              className={inputClassName}
            />
          </FormField>

          <FormField label="Status Date" required>
            <input
              type="date"
              name="statusdate"
              value={formData.statusdate}
              onChange={handleChange}
              required
              className={inputClassName}
            />
          </FormField>
        </div>

        <div className="mt-5 space-y-5">
          <FormField label="Issue Description" required>
            <textarea
              name="issuedescription"
              value={formData.issuedescription}
              onChange={handleChange}
              required
              rows={4}
              placeholder="Describe the issue in detail..."
              className={textareaClassName}
            />
          </FormField>

          <FormField label="Solution Description">
            <textarea
              name="solutiondescription"
              value={formData.solutiondescription}
              onChange={handleChange}
              rows={3}
              placeholder="Describe the proposed or applied solution (optional)"
              className={textareaClassName}
            />
          </FormField>

          <FormField label="Tech Comments">
            <textarea
              name="techcomments"
              value={formData.techcomments}
              onChange={handleChange}
              rows={3}
              placeholder="Internal technical notes or comments (optional)"
              className={textareaClassName}
            />
          </FormField>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700">
              Attachments
            </label>
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/80 px-4 py-8 text-center transition hover:border-primary/30 hover:bg-primary/5"
            >
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white text-primary shadow-sm">
                <Upload className="h-5 w-5" />
              </div>
              <p className="text-sm font-medium text-gray-800">
                Drag and drop files here, or{" "}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="font-semibold text-primary underline-offset-2 hover:underline"
                >
                  browse
                </button>
              </p>
              <p className="mt-1 text-xs text-gray-500">
                You can attach multiple files (max 50MB each)
              </p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
              />
            </div>

            {fileError && (
              <p className="mt-2 text-sm text-amber-700">{fileError}</p>
            )}

            {attachments.length > 0 && (
              <ul className="mt-4 divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white">
                {attachments.map((item) => (
                  <li
                    key={item.id}
                    className="flex items-center justify-between gap-3 px-4 py-3"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Paperclip className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-900">
                          {item.file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(item.file.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(item.id)}
                      className="rounded-lg p-2 text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                      aria-label={`Remove ${item.file.name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="mt-8 flex flex-col-reverse gap-3 border-t border-gray-100 pt-5 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={resetForm}
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <X className="h-4 w-4" />
            Clear Form
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Ticket className={`h-4 w-4 ${submitting ? "animate-pulse" : ""}`} />
            {submitting ? "Submitting..." : "Submit Ticket"}
          </button>
        </div>
      </form>
      <SuccessModal data={successModal} onClose={closeSuccessModal} />
    </PageShell>
  );
};

export default AddTicket;
